import Client from './KomodoClient';
import { PAGE_ALL, pagerRows } from './Resource';

// komodo MaterialController (api/material) + MaterialSortController (api/materialsort): the product
// list / editor endpoints used by the PSI "Products" pages.

const enc = encodeURIComponent;
const base = '/api/material';
const sortBase = '/api/materialsort';

// BillCodeConsts.Gy_MaterialBillNumber: the number sequence behind a new product's identity code.
export const GY_MATERIAL_BILL_NUMBER = '2042';

// MaterialQueryScopeType
export const QUERY_SCOPE = {
	ALL: 0,
	COMPANY_AVAILABLE: 1,
	NEED_COMPANY_TO_APPLY: 2,
	LIBRARY: 3,
};

// MaterialAssignTypeEnum
export const ASSIGN_TYPE = {
	ASSIGN_REQUIRED: 1,
	GLOBAL_PRODUCT: 2,
	COMPANY_PRIVATE: 3,
};

// PagerRecord → { rows, total } (total falls back to the row count for unpaged replies).
export const pager = (data) => {
	const rows = pagerRows(data);
	return {
		rows,
		total:
			data && typeof data.itemCount === 'number' ? data.itemCount : rows.length,
	};
};

const queryPager = (query) =>
	Client.post(`${base}/query-pager`, { ...PAGE_ALL, ...(query || {}) }).then(
		pager
	);
const skuQueryPager = (query) =>
	Client.post(`${base}/sku/query-pager`, {
		...PAGE_ALL,
		...(query || {}),
	}).then(pager);
// MaterialSelect with SearchAttributeTypeEnum.OnlyIncludeColor (the price rule "product variant"
// picker): only products with variants, one row per variant (MatInCode, ColorName). Active, not deleted.
export const VARIANT_QUERY = {
	SEARCH_ATTRIBUTE_ONLY_INCLUDE_COLOR: 1,
	ARCHIVE_ACTIVE: 0,
	DEL_NOT_DELETED: 3,
};
export const variantQuery = (searchText, deptCode, count, startIndex) => ({
	pageIndex: Math.floor((startIndex || 0) / count) + 1,
	pageItems: count,
	isPage: true,
	searchText: searchText || '',
	deptCode: deptCode || '',
	queryScopeType: deptCode
		? QUERY_SCOPE.COMPANY_AVAILABLE
		: QUERY_SCOPE.LIBRARY,
	searchAttributeType: VARIANT_QUERY.SEARCH_ATTRIBUTE_ONLY_INCLUDE_COLOR,
	archiveType: VARIANT_QUERY.ARCHIVE_ACTIVE,
	delType: VARIANT_QUERY.DEL_NOT_DELETED,
	showLibraryPricing: true,
});
const searchVariants = (searchText, deptCode, count = 20, startIndex = 0) =>
	Client.post(
		`${base}/query-pager`,
		variantQuery(searchText, deptCode, count, startIndex)
	).then(pagerRows);
const get = (materialCode) => Client.get(`${base}/${enc(materialCode)}`);
const create = (dto) => Client.post(base, dto);
const update = (materialCode, dto) =>
	Client.put(`${base}/${enc(materialCode)}`, dto);
const remove = (materialCode) => Client.del(`${base}/${enc(materialCode)}`);
// MaterialController archive: StopFlag goes through here so komodo can refuse a product that combos
// still use (MaterialInfoService.SetArchived).
const setArchived = (materialCode, archived) =>
	Client.put(`${base}/archive/${enc(materialCode)}`, { archived: !!archived });
// true when another product already uses the identity code.
const existIdentityCode = (identityCode) =>
	Client.put(`${base}/exist-identitycode/${enc(identityCode)}`);
const nextIdentityCode = () =>
	Client.get(`/api/bil-number/next-number/${GY_MATERIAL_BILL_NUMBER}`);
// Assign library products to the current company (BranchId header).
const companyUse = (materialCodes) =>
	Client.post(`${base}/company-use`, materialCodes);
const getCompany = (materialCode) =>
	Client.get(`${base}/company/${enc(materialCode)}`);
const companyQueryList = (materialCode, deptCodes) =>
	Client.post(`${base}/company/${enc(materialCode)}/query-list`, deptCodes);
const updateCompany = (materialCode, dto) =>
	Client.put(`${base}/company/${enc(materialCode)}`, dto);
const removeCompany = (materialCode) =>
	Client.del(`${base}/company/${enc(materialCode)}`);
// { materialCode, deptCode } → MaterialReferInfoDTO (name, unit, prices) for selectors.
const referQueryOne = (query) => Client.post(`${base}/refer/query-one`, query);
// { deptCode, items: [{ productCode, barcode }] } → MaterialListSimpleDTO[] (CSV selector).
const batchQueryList = (query) =>
	Client.post(`${base}/batch/query-list`, query);
const wareQueryList = (query) => Client.post(`${base}/ware/query-list`, query);
const updateWares = (materialCode, materialWares) =>
	Client.put(`${base}/ware/${enc(materialCode)}`, { materialWares });
const linkedMaterialSorts = (materialCodes) =>
	Client.post(`${base}/linked-material-sorts/query-list`, { materialCodes });

// Barcodes / supplier item codes (EncodingRelationController): loaded and saved apart from the product.
const encodingRelations = (materialCode) =>
	Client.post('/api/encoding-relation/query-list', { materialCode });
const saveEncodingRelations = (materialCode, rows) =>
	Client.post(`/api/encoding-relation/${enc(materialCode)}`, rows);
// DefineGlobalFamilyPromotionList: merchant-wide family promotions (read-only selectors).
const familyPromotions = () =>
	Client.post('/api/family-promotion/query-pager', { ...PAGE_ALL }).then(
		pagerRows
	);

// Product catalog tree. `list()` is scoped to the current company through the BranchId header.
const sorts = {
	list: (deptCode) =>
		Client.get(
			deptCode ? `${sortBase}/company/${enc(deptCode)}` : `${sortBase}/company`
		),
	listAll: () => Client.get(sortBase),
	get: (code) => Client.get(`${sortBase}/${enc(code)}`),
	create: (dto) => Client.post(sortBase, dto),
	update: (code, dto) => Client.put(`${sortBase}/${enc(code)}`, dto),
	// Company-scoped node (DeptCode set): sub levels become company custom categories too.
	updateCompany: (code, dto) =>
		Client.put(`${sortBase}/company/${enc(code)}`, dto),
	remove: (code) => Client.del(`${sortBase}/${enc(code)}`),
};

export default {
	GY_MATERIAL_BILL_NUMBER,
	QUERY_SCOPE,
	ASSIGN_TYPE,
	pager,
	queryPager,
	skuQueryPager,
	searchVariants,
	get,
	create,
	update,
	remove,
	setArchived,
	existIdentityCode,
	nextIdentityCode,
	companyUse,
	getCompany,
	companyQueryList,
	updateCompany,
	removeCompany,
	referQueryOne,
	batchQueryList,
	wareQueryList,
	updateWares,
	linkedMaterialSorts,
	encodingRelations,
	saveEncodingRelations,
	familyPromotions,
	sorts,
};
